import { AddImage, Delete, Pencil } from "assets/icons/Common";
import { Button, Col, IconButton, IconButtonInverted, Row } from "components/primitives";
import { InnerContainer } from "components/settings";
import EmojiPicker, { EmojiClickData, EmojiStyle } from "emoji-picker-react";
import { FC, useState } from "react";
import { Form, Accordion, Card } from "react-bootstrap";
import styled from "styled-components";

import { CalendarItemData } from "./interfaces";

interface Props {
  event: CalendarItemData;
  deleteSelectedEvent: (id: string) => void;
  onSubmit?: (newEvent: CalendarItemData) => void;
}

export const Event: FC<Props> = ({ event: currentEvent, deleteSelectedEvent, onSubmit }) => {
  const [event, setEvent] = useState<CalendarItemData>(currentEvent);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const updateEvent = (newEvent: Partial<CalendarItemData>) => {
    setEvent({
      ...event,
      ...newEvent,
    });
  };

  const onChangeName = (name: string) => {
    updateEvent({ event: name });
  };

  const onImageSelected = (emojiData: EmojiClickData) => {
    setShowEmojiPicker(false);
    updateEvent({ image: emojiData.emoji });
  };

  const onEditSubmit = () => {
    if (onSubmit && event) {
      onSubmit(event);
    }
  };

  const isEventValid = () => {
    return Boolean(event?.image);
  };

  const isEventChanged = () => {
    return (
      event?.event?.trim() !== currentEvent?.event?.trim() || event?.image !== currentEvent?.image
    );
  };

  return (
    <EventCard>
      <CardHeader>
        <InnerContainer>
          <EventTitle isValid={isEventValid()}>
            {isEventValid() ? event?.event : "New Event"}
          </EventTitle>
          {event?.image && <Image>{event?.image}</Image>}
        </InnerContainer>
        <ActionRow>
          <Accordion.Toggle
            as={Button}
            eventKey={event.id}
            style={{
              width: "24px",
              height: "24px",
              padding: "4px",
              boxShadow: "none",
            }}
          >
            <Pencil width="100%" height="100%" color="var(--widget-primary)" />
          </Accordion.Toggle>
          <IconButton
            accentColor="var(--danger)"
            Icon={Delete}
            onClick={() => deleteSelectedEvent(event.id)}
            style={{ height: "24px", padding: "4px" }}
            withShadow={false}
          />
        </ActionRow>
      </CardHeader>
      <Accordion.Collapse eventKey={event.id}>
        <EditFormCol>
          <Form.Group>
            <Form.Label>
              Name <small>(Max characters: 20)</small>
            </Form.Label>
            <Form.Control
              type="text"
              maxLength={20}
              value={event.event}
              onChange={(e) => onChangeName(e.target.value)}
            />
          </Form.Group>
          <LowerRow>
            <Col style={{ position: "relative" }}>
              <p>Image</p>
              {event.image ? (
                <Row style={{ height: "45px", alignItems: "center", gap: "5px" }}>
                  <Button
                    style={{
                      boxShadow: "none",
                      width: "45px",
                      height: "45px",
                      fontSize: "15pt",
                    }}
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    {event.image}
                  </Button>
                  <IconButtonInverted
                    accentColor="var(--danger)"
                    Icon={Delete}
                    onClick={() => updateEvent({ image: "" })}
                    style={{ height: "70%", padding: "8px" }}
                    withShadow={false}
                  />
                </Row>
              ) : (
                <EmojiButton
                  withShadow={false}
                  Icon={AddImage}
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                />
              )}
              <EmojiPickerContainer show={showEmojiPicker}>
                <EmojiPicker
                  skinTonesDisabled={true}
                  onEmojiClick={onImageSelected}
                  emojiStyle={EmojiStyle.NATIVE}
                />
              </EmojiPickerContainer>
            </Col>
          </LowerRow>
          <Footer>
            <FooterButton
              color="var(--primary)"
              onClick={onEditSubmit}
              disabled={!isEventValid() || !isEventChanged()}
            >
              Save
            </FooterButton>
          </Footer>
        </EditFormCol>
      </Accordion.Collapse>
    </EventCard>
  );
};

const EventCard = styled(Card)`
  overflow: visible !important;
`;

const CardHeader = styled(Card.Header)`
  display: flex;
  justify-content: space-between;
`;

const EditFormCol = styled(Col)`
  padding: 0.75rem 1.25rem;
`;

const Image = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  text-align: center;
`;

const ActionRow = styled(Row)`
  justify-content: flex-end;
  gap: 10px;
`;

const LowerRow = styled(Row)`
  margin-bottom: 1rem;
  gap: 50px;
`;

const EmojiButton = styled(IconButton)`
  width: 45px;
  height: 45px;
`;

const Footer = styled(Row)`
  justify-content: flex-end;
  gap: 10px;
`;

const FooterButton = styled(Button)<{ color: string }>`
  background-color: ${(props) => props.color};
  box-shadow: none;
  color: black;
  width: 20%;
`;

const EmojiPickerContainer = styled.div<{ show: boolean }>`
  display: ${(props) => (props.show ? "initial" : "none")};
  position: absolute;
  left: 50px;
  top: 0;
  transform: translate(0, -50%);
  z-index: 10;
`;

const EventTitle = styled.p<{ isValid: boolean }>`
  color: ${(props) => (props.isValid ? "#000" : "#777")};
  font-size: 18px;
  line-height: 1.2;
`;
